import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SERVICES, STATIONS } from '../../utils/kitchenTasks';
import { STATES, SOURCES, quickRange } from './reportUtils';

const ALL = '__all';
const fieldCls = 'h-11 rounded-xl bg-gray-100 border-gray-200 text-gray-800 text-sm';
const QUICK = [['today', "Aujourd'hui"], ['7d', '7 derniers jours'], ['week', 'Semaine en cours'], ['month', 'Mois en cours']];

const FilterSelect = ({ label, value, onChange, options, testId, allLabel = 'Tous' }) => (
  <div>
    <Label className="text-gray-500 text-xs">{label}</Label>
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? '' : v)}>
      <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white border-gray-200">
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const DateInput = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-gray-500 text-xs">{label}</Label>
    <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={`mt-1 block w-full px-3 border ${fieldCls} focus:outline-none focus:ring-2 focus:ring-neon-pink/50`} data-testid={testId} />
  </div>
);

const ReportFilters = ({ filters, onChange, collaborators, today, onRefresh, loading }) => {
  const set = (k) => (v) => onChange({ ...filters, [k]: v });
  const isQuick = (k) => { const r = quickRange(k, today); return r && r.from === filters.from && r.to === filters.to; };
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-3 md:p-4 space-y-3" data-testid="report-filters">
      <div className="flex flex-wrap gap-2">
        {QUICK.map(([k, l]) => (
          <button key={k} type="button" onClick={() => onChange({ ...filters, ...quickRange(k, today) })} data-testid={`report-quick-${k}`}
            className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${isQuick(k) ? 'bg-neon-pink text-gray-900 border-neon-pink' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3">
        <DateInput label="Du" value={filters.from} onChange={set('from')} testId="report-from-input" />
        <DateInput label="Au" value={filters.to} onChange={set('to')} testId="report-to-input" />
        <FilterSelect label="Service" value={filters.service} onChange={set('service')} options={SERVICES} testId="report-service-select" />
        <FilterSelect label="Poste" value={filters.category} onChange={set('category')} options={STATIONS.map(s => ({ value: s.id, label: s.label }))} testId="report-category-select" />
        <FilterSelect label="Collaborateur" value={filters.assigned_user_id} onChange={set('assigned_user_id')} options={collaborators.map(c => ({ value: c.id, label: c.name }))} testId="report-collab-select" />
        <FilterSelect label="État" value={filters.state} onChange={set('state')} options={STATES} testId="report-state-select" />
        <FilterSelect label="Source" value={filters.source} onChange={set('source')} options={SOURCES} testId="report-source-select" allLabel="Toutes" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="h-10 rounded-full border-gray-200 text-gray-700" data-testid="report-refresh-btn">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />Actualiser
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onChange({ ...filters, service: '', category: '', assigned_user_id: '', state: '', source: '' })} className="h-10 rounded-full text-gray-500" data-testid="report-reset-btn">
          Réinitialiser les filtres
        </Button>
      </div>
    </div>
  );
};

export default ReportFilters;
